import React from 'react';
import styled from 'styled-components';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';

import ArrowForward from '@mui/icons-material/ArrowForward';

import { Tracklog } from 'ff/debrief/models';
import InfoMenu from 'ff/debrief/components/segments/InfoMenu';

type Props = Pick<
    Tracklog,
    | 'accountUuid'
    | 'username'
    | 'derivedOrigin'
    | 'derivedDestination'
    | 'timestampStart'
    | 'tailNumber'
    | 'trackUuid'
> & {
    action?: React.ReactNode;
    onBackClick?: () => void;
    hideTailNumber?: boolean;
};

const FlightInfo: React.FC<Props> = props => {
    const date =
        props.timestampStart &&
        new Date(props.timestampStart).toLocaleString(
            undefined,
            dateFormatOptions
        );
    return (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            borderBottom="1px solid rgba(0, 0, 0, 0.10)"
            p={1}
        >
            {props.action}
            <Stack width="175px">
                <Terminals
                    from={props.derivedOrigin}
                    to={props.derivedDestination}
                />
                <DateField>{date || <Skeleton width="180px" />}</DateField>
                {!props.hideTailNumber && (
                    <AircraftField>
                        {props.tailNumber || <Skeleton width="100px" />}
                    </AircraftField>
                )}
            </Stack>
            <InfoMenu />
        </Stack>
    );
};

const Terminals: React.FC<{ to: string; from: string }> = ({ to, from }) => (
    <TerminalsContainer>
        <TerminalField>
            {from || <Skeleton width="50px" height="24px" />}
        </TerminalField>
        <ArrowForward sx={{ marginX: '6px' }} fontSize="small" />
        <TerminalField>
            {to || <Skeleton width="50px" height="24px" />}
        </TerminalField>
    </TerminalsContainer>
);

const TerminalsContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    max-width: 200px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const TerminalField = styled(Typography)`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #1c1c1e;
    font-size: 16px;
    font-weight: 700;
    line-height: 21px;
`;

const DateField = styled(Typography)`
    color: #646468;
    font-size: 13px;
    font-weight: 400;
    font-style: normal;
    line-height: normal;
`;

const AircraftField = styled(Typography)`
    color: #646468;
    font-size: 13px;
    font-weight: 400;
    font-style: normal;
    line-height: normal;
`;

const dateFormatOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    timeZoneName: 'short',
};

export default FlightInfo;
